import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MediaCard from "@/components/base/MediaCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaItems } from "@/mocks/media";
import { newsItems } from "@/mocks/news";
import type { NewsItem } from "@/types/content";
import FilterPanel from "./components/FilterPanel";

type CategoryKind = "movie" | "tv" | "news" | "all";

const kindConfig: Record<
  CategoryKind,
  { title: string; intro: string; seoTitle: string; seoDesc: string; countUnit: string }
> = {
  movie: {
    title: "电影",
    intro:
      "从院线新片到影史经典，按类型、地区与年份快速筛选。每部影片都配有完整主创信息与剧情简介。",
    seoTitle: "天马传媒电影资源 - 分类浏览",
    seoDesc:
      "浏览天马传媒精选电影资源，支持类型、地区、年份多维筛选，轻松找到喜欢的影片。",
    countUnit: "部影片",
  },
  tv: {
    title: "电视剧",
    intro:
      "热播剧集、新番与口碑老剧一网打尽。可按题材与地区筛选，查看集数、主创与剧情走向。",
    seoTitle: "天马传媒电视剧资源 - 分类浏览",
    seoDesc:
      "浏览天马传媒精选电视剧资源，支持类型、地区、年份多维筛选，发现值得追的好剧。",
    countUnit: "部剧集",
  },
  news: {
    title: "影视资讯",
    intro:
      "行业动态、深度影评与幕后花絮。关注正在发生的事，也回头看一部作品是怎样被拍出来的。",
    seoTitle: "天马传媒影视资讯 - 行业动态与影评",
    seoDesc:
      "天马传媒影视资讯频道，汇集最新行业动态、深度影评与幕后花絮，按时间与热度排序浏览。",
    countUnit: "条资讯",
  },
  all: {
    title: "站内搜索",
    intro: "在全部影视作品与资讯中检索，支持片名、题材、主创与资讯关键词。",
    seoTitle: "站内搜索 - 天马传媒",
    seoDesc: "在天马传媒全站检索影视作品与资讯，支持关键词筛选，快速定位想看的内容。",
    countUnit: "条结果",
  },
};

const sortOptions = [
  { key: "hot", label: "热门" },
  { key: "latest", label: "最新" },
  { key: "rating", label: "评分" },
];

const newsSortOptions = [
  { key: "latest", label: "最新" },
  { key: "hot", label: "热门" },
];

const PAGE_SIZE = 6;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind: CategoryKind = (
    ["movie", "tv", "news", "all"].includes(type ?? "") ? type : "movie"
  ) as CategoryKind;
  const config = kindConfig[kind];
  const keyword = searchParams.get("q")?.trim() ?? "";
  const genreParam = searchParams.get("genre")?.trim() ?? "";

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [newsCategory, setNewsCategory] = useState("全部");
  const [sort, setSort] = useState(kind === "news" ? "latest" : "hot");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setGenre(genreParam || "全部");
    setRegion("全部");
    setYear("全部");
    setNewsCategory("全部");
    setSort(kind === "news" ? "latest" : "hot");
    setVisible(PAGE_SIZE);
  }, [kind, genreParam]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [genre, region, year, newsCategory, sort, keyword]);

  const works = useMemo(() => {
    if (kind === "news") return [];
    if (kind === "all") return mediaItems;
    return mediaItems.filter((item) => item.type === kind);
  }, [kind]);

  const genreOptions = useMemo(
    () => Array.from(new Set(works.flatMap((item) => item.genres))),
    [works],
  );
  const regionOptions = useMemo(
    () => Array.from(new Set(works.map((item) => item.region))),
    [works],
  );
  const yearOptions = useMemo(
    () => Array.from(new Set(works.map((item) => String(item.year)))).sort((a, b) => Number(b) - Number(a)),
    [works],
  );

  const filteredWorks = useMemo(() => {
    const lower = keyword.toLowerCase();
    const list = works.filter((item) => {
      const matchKeyword =
        !lower ||
        item.title.toLowerCase().includes(lower) ||
        item.genres.some((g) => g.toLowerCase().includes(lower)) ||
        item.director.toLowerCase().includes(lower) ||
        item.cast.some((c) => c.toLowerCase().includes(lower));
      const matchGenre = genre === "全部" || item.genres.includes(genre);
      const matchRegion = region === "全部" || item.region === region;
      const matchYear = year === "全部" || String(item.year) === year;
      return matchKeyword && matchGenre && matchRegion && matchYear;
    });

    return list.sort((a, b) => {
      if (sort === "latest") return new Date(b.updated).getTime() - new Date(a.updated).getTime();
      if (sort === "rating") return b.rating - a.rating;
      return b.hot - a.hot;
    });
  }, [works, keyword, genre, region, year, sort]);

  const filteredNews = useMemo(() => {
    const lower = keyword.toLowerCase();
    const list = newsItems.filter((item) => {
      const matchKeyword =
        !lower ||
        item.title.toLowerCase().includes(lower) ||
        item.summary.toLowerCase().includes(lower) ||
        item.category.includes(keyword);
      const matchCategory = newsCategory === "全部" || item.category === newsCategory;
      return matchKeyword && matchCategory;
    });
    return list.sort((a, b) => {
      if (sort === "hot") return b.hot - a.hot;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [keyword, newsCategory, sort]);

  const allMatchedNews = useMemo(() => {
    if (kind !== "all" || !keyword) return [];
    const lower = keyword.toLowerCase();
    return newsItems.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) || item.summary.toLowerCase().includes(lower),
    );
  }, [kind, keyword]);

  const total =
    kind === "news"
      ? filteredNews.length
      : kind === "all"
        ? filteredWorks.length + allMatchedNews.length
        : filteredWorks.length;
  const visibleWorks = filteredWorks.slice(0, visible);
  const visibleNews = filteredNews.slice(0, visible);
  const hasMore =
    kind === "news" ? filteredNews.length > visible : filteredWorks.length > visible;

  // 关键词同时参与作品与资讯检索
  const activeFilters = [
    genre !== "全部" ? genre : "",
    region !== "全部" ? region : "",
    year !== "全部" ? year : "",
    newsCategory !== "全部" ? newsCategory : "",
    keyword ? `关键词：${keyword}` : "",
  ].filter(Boolean);

  const activeCount = [
    genre !== "全部",
    region !== "全部",
    year !== "全部",
    newsCategory !== "全部",
    Boolean(keyword),
  ].filter(Boolean).length;

  const groups =
    kind === "news"
      ? [{ key: "newsCategory", label: "资讯分类", value: newsCategory, options: ["资讯", "影评", "幕后"] }]
      : [
          { key: "genre", label: "类型", value: genre, options: genreOptions },
          { key: "region", label: "地区", value: region, options: regionOptions },
          { key: "year", label: "年份", value: year, options: yearOptions },
        ];

  const handleFilterChange = (key: string, value: string) => {
    if (key === "genre") setGenre(value);
    if (key === "region") setRegion(value);
    if (key === "year") setYear(value);
    if (key === "newsCategory") setNewsCategory(value);
  };

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setNewsCategory("全部");
    setSort(kind === "news" ? "latest" : "hot");
    if (keyword) {
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      setSearchParams(next);
    }
  };

  const clearKeyword = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next);
  };

  const pageTitle = keyword ? `“${keyword}”的搜索结果 - 天马传媒` : config.seoTitle;
  const pageDesc = keyword
    ? `天马传媒站内搜索“${keyword}”的结果，共 ${total} 条相关内容，可按类型、地区与年份继续筛选。`
    : config.seoDesc;

  usePageMeta(pageTitle, pageDesc, `${config.title},影视筛选,天马传媒,${keyword || "影视作品"}`);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <section className="border-b border-background-200 bg-background-100">
          <div className="shell py-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer transition hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              {kind === "all" ? (
                <span className="text-foreground-800">站内搜索</span>
              ) : (
                <span className="text-foreground-800">{config.title}</span>
              )}
            </nav>

            <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                  {keyword ? `搜索：${keyword}` : config.title}
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  {config.intro}
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span className="rounded-md border border-background-200 bg-background-50 px-3 py-1.5">
                  共 <span className="font-medium text-primary-600">{total}</span> {config.countUnit}
                </span>
                {keyword && (
                  <button
                    type="button"
                    onClick={clearKeyword}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-accent-200 bg-accent-50 px-3 py-1.5 text-accent-800 transition hover:bg-accent-100"
                  >
                    <i className="ri-close-line"></i>
                    {keyword}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="shell py-8 md:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <aside className="w-full shrink-0 lg:w-[264px]">
              <button
                type="button"
                onClick={() => setMobileFilterOpen((open) => !open)}
                className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-800 lg:hidden"
                aria-expanded={mobileFilterOpen}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-600"></i>
                  筛选条件
                  {activeCount > 0 && (
                    <span className="rounded-full bg-accent-500 px-1.5 text-[10px] text-primary-950">
                      {activeCount}
                    </span>
                  )}
                </span>
                <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              </button>

              <div className={`${mobileFilterOpen ? "mt-3 block" : "hidden"} lg:mt-0 lg:block`}>
                <FilterPanel
                  groups={groups}
                  onChange={handleFilterChange}
                  onReset={resetFilters}
                  activeCount={activeCount}
                />

                <div className="mt-4 hidden rounded-lg border border-primary-100 bg-primary-50/60 p-4 lg:block">
                  <p className="text-xs tracking-[0.16em] text-primary-600">编辑推荐</p>
                  <h3 className="mt-1.5 font-heading text-base font-semibold text-foreground-950">
                    不知道看什么？
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                    试试我们的主题片单，按氛围而不是类型来挑片。
                  </p>
                  <Link
                    to="/specials"
                    className="mt-3 inline-flex cursor-pointer items-center gap-1 text-xs text-primary-600 transition hover:text-accent-600"
                  >
                    浏览专题
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
                  {activeFilters.length === 0 ? (
                    <span>当前展示全部内容</span>
                  ) : (
                    activeFilters.map((filter) => (
                      <span
                        key={filter}
                        className="rounded border border-background-200 bg-background-100 px-2 py-1 text-foreground-600"
                      >
                        {filter}
                      </span>
                    ))
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序</span>
                  <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
                    {(kind === "news" ? newsSortOptions : sortOptions).map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setSort(option.key)}
                        className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition ${
                          sort === option.key
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600 hover:text-primary-600"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {total === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-20 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-400">
                    <i className="ri-search-eye-line"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg text-foreground-950">
                    没有找到符合条件的内容
                  </h2>
                  <p className="mt-2 max-w-sm text-sm text-foreground-600">
                    试试更换关键词，或减少筛选条件。也可以直接浏览全部{kind === "news" ? "资讯" : "作品"}。
                  </p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600"
                  >
                    重置筛选条件
                  </button>
                </div>
              ) : kind === "news" ? (
                <ul className="mt-5 divide-y divide-background-200">
                  {visibleNews.map((item: NewsItem) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/${item.id}`}
                        className="group flex cursor-pointer flex-col gap-4 py-5 sm:flex-row"
                      >
                        <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[104px] sm:w-40">
                          <img
                            src={item.image}
                            alt={`${item.title}资讯配图`}
                            title={`${item.title} - 天马传媒影视资讯`}
                            className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded border border-primary-100 bg-primary-50 px-1.5 py-0.5 text-[10px] text-primary-700">
                              {item.category}
                            </span>
                            <span className="text-[11px] text-foreground-500">{item.date}</span>
                          </div>
                          <h2 className="mt-1.5 line-clamp-1 text-sm font-medium text-foreground-950 transition group-hover:text-primary-600 md:text-base">
                            {item.title}
                          </h2>
                          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-[13px]">
                            {item.summary}
                          </p>
                          <span className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 opacity-0 transition group-hover:opacity-100">
                            阅读全文
                            <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                  {visibleWorks.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
              )}

              {kind === "all" && allMatchedNews.length > 0 && (
                <div className="mt-10">
                  <div className="flex items-center justify-between border-b border-background-200 pb-3">
                    <h2 className="font-heading text-base font-semibold text-foreground-950">
                      相关资讯
                    </h2>
                    <span className="text-xs text-foreground-500">
                      {allMatchedNews.length} 条
                    </span>
                  </div>
                  <ul className="divide-y divide-background-200">
                    {allMatchedNews.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/${item.id}`}
                          className="group flex cursor-pointer items-center gap-4 py-4"
                        >
                          <span className="rounded border border-accent-200 bg-accent-50 px-1.5 py-0.5 text-[10px] text-accent-800">
                            {item.category}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="line-clamp-1 text-sm text-foreground-900 transition group-hover:text-primary-600">
                              {item.title}
                            </span>
                            <span className="mt-0.5 block text-[11px] text-foreground-500">
                              {item.date}
                            </span>
                          </span>
                          <i className="ri-arrow-right-line text-foreground-300 transition group-hover:translate-x-1 group-hover:text-accent-600"></i>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {hasMore && (
                <div className="mt-8 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVisible((count) => count + PAGE_SIZE)}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                  >
                    加载更多
                  </button>
                  <span className="text-[11px] text-foreground-500">
                    已展示 {kind === "news" ? visibleNews.length : visibleWorks.length} / {total} 条
                  </span>
                </div>
              )}

              {!hasMore && total > 0 && (
                <p className="mt-8 text-center text-[11px] tracking-widest text-foreground-400">
                  — 已经到底了 —
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}